import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { confirmMsg, errorMsg } from '../constants/messages'
import {
  PasswordResetErrorIntervalMinutes,
  PasswordResetErrorLimitTimes,
} from '../constants/passwordResetLimit'
import { useAuth } from '../hooks/useAuth'
import { useC001IdTokenGet } from '../hooks/useC001IdTokenGet'
import { useC101UserGet } from '../hooks/useC101UserGet'
import { useC102UserUpdate } from '../hooks/useC102UserUpdate'
import { useC103PasswordUpdate } from '../hooks/useC103PasswordUpdate'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import { strictPasswordSchema } from '../schema/loginSchema'
import { LanguageContext } from '../util/LanguageContext'

// 新パスワード入力画面
export const NewPassword = () => {
  // auth
  const auth = useAuth()
  const c001 = useC001IdTokenGet()
  const c101 = useC101UserGet()
  const c102 = useC102UserUpdate()
  const c103 = useC103PasswordUpdate()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // クエリパラメータ取得
  const search = useLocation().search

  // const [emailLinkloginId, setEmailLinkLoginId] = useState("");
  // const [emailLinkJwt, setEmailLinkJwt] = useState("");

  const [nextPage, setNextPage] = useState('')

  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')

  // const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showVerifyPassword, setShowVerifyPassword] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)

  // const [errorCurrentPassword, setErrorCurrentPassword] = useState("");
  const [errorNewPassword, setErrorNewPassword] = useState('')
  const [errorConfirmPassword, setErrorConfirmPassword] = useState('')

  // クエリパラメータ取得
  const query = new URLSearchParams(search)
  const email = query.get('email')
  const loginId = query.get('login_id')
  const reset = query.get('reset')
  const token = query.get('token')

  const validateNewPassword = async (value: string) => {
    try {
      await strictPasswordSchema.validate(value)
      if (confirmPassword !== '') {
        if (confirmPassword === value) {
          if (errorConfirmPassword === errorMsg.PASSWORD_UNMATCH_ERROR) {
            setErrorConfirmPassword('')
          }
          setErrorNewPassword('')
        } else {
          setErrorNewPassword(errorMsg.PASSWORD_UNMATCH_ERROR)
        }
      } else {
        setErrorNewPassword('')
      }
    } catch (err: any) {
      setErrorNewPassword(err.message)
    }
  }
  const validateConfirmPassword = async (value: string) => {
    try {
      await strictPasswordSchema.validate(value)
      if (newPassword === value) {
        if (errorNewPassword === errorMsg.PASSWORD_UNMATCH_ERROR) {
          setErrorNewPassword('')
        }
        setErrorConfirmPassword('')
      } else {
        setErrorConfirmPassword(errorMsg.PASSWORD_UNMATCH_ERROR)
      }
    } catch (err: any) {
      setErrorConfirmPassword(err.message)
    }
  }

  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value)
    validateNewPassword(event.target.value)
  }

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value)
    validateConfirmPassword(event.target.value)
  }

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(prev => !prev)
  }

  const handleToggleVerifyPasswordVisibility = () => {
    setShowVerifyPassword(prev => !prev)
  }

  const navigate = useNavigate()
  const onClickCancel = () => {
    navigate('/login')
  }
  const onClickConfirm = () => {
    navigate(nextPage)
  }

  const onClickPasswordEntry = async () => {
    // 失敗回数変数
    const errCntName = 'ErrorCount'
    // 失敗時刻変数
    const errTimeName = 'ErrorTime'
    // ローカルストレージから失敗回数と失敗時刻を取得する
    const errCnt = localStorage.getItem(errCntName)
    const errTime = localStorage.getItem(errTimeName)
    let errorCount = 0
    const currentTime: number = Date.now()
    if (errCnt) {
      errorCount = Number.parseInt(errCnt, 10)
    }
    if (errTime) {
      const savedTime: number = Number.parseInt(errTime, 10)
      const thresholdTime: number = savedTime + PasswordResetErrorIntervalMinutes * 60 * 1000
      if (currentTime > thresholdTime) {
        errorCount = 0
      }
    }
    if (errorCount >= PasswordResetErrorLimitTimes) {
      setErrorDialog('label_reached_maximum')
      setOpenDialog(true)
      setIsDisabled(false)
      return
    }
    // email で IDトークン取得
    const resultToken = await c001.execIdTokenGet(loginId!, undefined, email!)
    if (resultToken.success) {
      const jwt = resultToken.message
      // パスワード更新
      const resultPass = await c103.execPasswordUpdate(loginId!, '', newPassword, token!, jwt)

      if (resultPass.success) {
        // 制限チェックがあれば削除する
        if (errCnt) {
          localStorage.removeItem(errCntName)
        }
        if (errTime) {
          localStorage.removeItem(errTimeName)
        }
        // 新パスワードで ログイン
        const result = await auth.signIn(loginId!, newPassword)
        if (result.success) {
          // ユーザー更新(メールリンク認証完了)
          const resultUpdate = await c102.execUserUpdate(loginId!, undefined, true, result.message)
          if (resultUpdate.success) {
            // console.log("execUserUpdate successed.");
            // ユーザー取得
            const resultUser = await c101.execUserGet(loginId!, result.message!)
            if (resultUser.success && resultUser.info) {
              const userInfo = resultUser.info
              auth.setDeviceId(userInfo.device_id ?? '')
              auth.setRole(userInfo.role)
              auth.setRhq(userInfo.rhq)
              auth.setSalesCompany(userInfo.sales_company ?? '')
              // 次画面設定
              if (reset === '1') {
                setNextPage('/login')
              } else {
                if (userInfo.role === 1) {
                  setNextPage('/phrase')
                } else {
                  setNextPage('/device')
                }
              }
              setConfirmMessage(confirmMsg.PASSWORD_UPDATEED)
              setOpenConfirmDialog(true)
            } else {
              setErrorDialog(resultUser.message!)
              setOpenDialog(true)
            }
          } else {
            setErrorDialog(resultUpdate.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(result.message!)
          setOpenDialog(true)
        }
      } else {
        errorCount++
        localStorage.setItem(errCntName, errorCount.toString())
        localStorage.setItem(errTimeName, currentTime.toString())
        setErrorDialog(resultPass.message!)
        setOpenDialog(true)
      }
    } else {
      errorCount++
      localStorage.setItem(errCntName, errorCount.toString())
      localStorage.setItem(errTimeName, currentTime.toString())
      setErrorDialog(resultToken.message!)
      setOpenDialog(true)
    }
  }
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  // 入力チェック
  useEffect(() => {
    if (
      newPassword !== '' &&
      confirmPassword !== '' &&
      errorNewPassword === '' &&
      errorConfirmPassword === '' &&
      newPassword === confirmPassword
    ) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [newPassword, confirmPassword, errorNewPassword, errorConfirmPassword])

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      {openConfirmDialog && (
        <ModalConfirmDialog
          open={openConfirmDialog}
          text={confirmMessage}
          onClick={onClickConfirm}
        />
      )}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
        }}
      >
        {(c001.loading === loadStatus.LOADING ||
          c101.loading === loadStatus.LOADING ||
          c102.loading === loadStatus.LOADING ||
          c103.loading === loadStatus.LOADING ||
          auth.loading === loadStatus.LOADING) && <CircularProgress />}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'auto',
          marginTop: '80px',
        }}
      >
        <Paper
          elevation={5}
          sx={{
            height: '810px',
            width: '787px',
            boxShadow: '0px 2px 2px #00000026',
            borderRadius: '10px',
          }}
        >
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Stack direction="column">
              <Box mt="111px" display="flex" justifyContent="center">
                <CotopatImage width="172px" height="44px" />
              </Box>
              <Typography
                sx={{
                  mt: '80px',
                  top: '315px',
                  left: '759px',
                  font: 'normal normal normal 21px/29px Noto Sans JP',
                }}
              >
                {translation['label_new_password']}
              </Typography>
              <TextField
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                error={!!errorNewPassword}
                helperText={errorNewPassword ? translation[errorNewPassword] : ' '}
                onChange={handleNewPasswordChange}
                InputProps={{
                  endAdornment: newPassword ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleNewPasswordVisibility}>
                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { background: '#F4F4F4' },
                }}
                sx={{
                  mt: '8px',
                  width: '402px',
                  // background: "#F4F4F4",
                  borderRadius: '10px',
                  'input::-ms-reveal': {
                    display: 'none' /* -ms-revealアイコンを非表示にする */,
                  },
                  '& .MuiFormHelperText-root': {
                    whiteSpace: 'pre-line',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: errorNewPassword ? 'red' : '#282828',
                    },
                  },
                }}
                variant="outlined"
              />

              <Typography
                sx={{
                  mt: '28px',
                  top: '315px',
                  left: '759px',
                  font: 'normal normal normal 21px/29px Noto Sans JP',
                }}
              >
                {translation['label_confirm_new_password']}
              </Typography>
              <TextField
                type={showVerifyPassword ? 'text' : 'password'}
                value={confirmPassword}
                error={!!errorConfirmPassword}
                helperText={errorConfirmPassword ? translation[errorConfirmPassword] : ' '}
                onChange={handleConfirmPasswordChange}
                InputProps={{
                  endAdornment: newPassword ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleVerifyPasswordVisibility}>
                        {showVerifyPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { background: '#F4F4F4' },
                }}
                sx={{
                  mt: '8px',
                  width: '402px',
                  // background: "#F4F4F4",
                  borderRadius: '10px',
                  'input::-ms-reveal': {
                    display: 'none' /* -ms-revealアイコンを非表示にする */,
                  },
                  '& .MuiFormHelperText-root': {
                    whiteSpace: 'pre-line',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: errorConfirmPassword ? 'red' : '#282828',
                    },
                  },
                }}
                variant="outlined"
              />
            </Stack>

            <Box mt={7} display="flex" gap="40px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{
                  width: '181px',
                  height: '49px',
                  border: '2px solid #282828',
                  borderRadius: '10px',
                  boxShadow: 0,
                  textAlign: 'center',
                  font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                  letterSpacing: '0px',
                  color: '#282828',
                  '&:hover': {
                    background: '#282828 0% 0% no-repeat padding-box',
                    opacity: 0.8,
                    boxShadow: 0,
                  },
                }}
                style={{ backgroundColor: '#F4F4F4' }}
                onClick={onClickCancel}
              >
                {translation['button_cancel']}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{
                  width: '181px',
                  height: '49px',
                  borderRadius: '10px',
                  boxShadow: 0,
                  textAlign: 'center',
                  font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                  letterSpacing: '0px',
                  color: '#FFFFFF',
                  '&:hover': {
                    background: '#282828 0% 0% no-repeat padding-box',
                    opacity: 0.8,
                    boxShadow: 0,
                  },
                }}
                disabled={isDisabled}
                style={{ backgroundColor: isDisabled ? '#F4F4F4' : '#282828' }}
                onClick={onClickPasswordEntry}
              >
                {translation['button_register']}
              </Button>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center" mt="140px" mb="30px">
              <Typography
                mb="2px"
                sx={{
                  font: 'normal normal normal 16px/24px Noto Sans JP',
                  textAlign: 'left',
                  letterSpacing: '0px',
                  color: '#282828',
                }}
              >
                {translation['copyright_info']}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  )
}
